"use client";
import React, { useState, useEffect } from "react";
import clsx from "clsx";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";
import { LuCirclePlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import AddAppointmentModal from "./AddApointmentModal";
import DayView from "./DayView";
import MonthView from "./MonthView";
import WeekView from "./WeekView";
import { REST_API_URL } from "../api/config";

const BASE_URL = REST_API_URL

const days = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (code) =>
  CURRENCY_SYMBOL_MAP[code] || code || "";

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

const fetchUserInfo = async (token) => {
  try {
    if (!token) return { currency: "USD" };
    
    const cachedData = localStorage.getItem("userInfo");
    if (cachedData) {
      const parsedData = JSON.parse(cachedData);
      const cacheTime = localStorage.getItem("userInfoTimestamp");
      const currentTime = new Date().getTime();
      if (cacheTime && (currentTime - parseInt(cacheTime)) < 3600000) {
        return parsedData;
      }
    }

    const headers = buildHeaders(token);
    
    const response = await fetch(`${BASE_URL}/auth/userinfo/`, {
      headers,
    });
    
    if (response.ok) {
      const userData = await response.json();
      
      localStorage.setItem("userInfo", JSON.stringify(userData));
      localStorage.setItem("userInfoTimestamp", new Date().getTime().toString());
      
      return userData;
    }
    return { currency: "USD" };
  } catch (error) {
    console.error("Error fetching user info:", error);
    return { currency: "USD" };
  }
};

const Calendar = () => {
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [direction, setDirection] = useState(0);
  const [view, setView] = useState("Month");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dayEvents, setDayEvents] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [userCurrencySymbol, setUserCurrencySymbol] = useState("$");

  const [summary, setSummary] = useState({
    price: 0,
    currency: "USD",
    currencySymbol: "$",
  });

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    const getUserInfo = async () => {
      if (token) {
        try {
          const userInfo = await fetchUserInfo(token);
          const currencyCode = userInfo?.currency || "USD";
          const currencySymbol = getCurrencySymbol(currencyCode);
          
          setUserCurrency(currencyCode);
          setUserCurrencySymbol(currencySymbol);
          
          setSummary(prev => ({
            ...prev,
            currency: currencyCode,
            currencySymbol: currencySymbol
          }));
        } catch (error) {
          console.error("Failed to fetch user info:", error);
        }
      }
    };
    
    getUserInfo();
  }, [token]);

  useEffect(() => {
    if (!token) {
      toast.error("Authorization required — please sign in again.");
      navigate("/sign-in");
    }
  }, [token, navigate]);

  const handlePrev = () => {
    setDirection(-1);
    if (view === "Month") setCurrentDate(subMonths(currentDate, 1));
    if (view === "Week") setSelectedDate(subWeeks(selectedDate, 1));
    if (view === "Day") setSelectedDate((p) => subDays(p, 1));
  };

  const handleNext = () => {
    setDirection(1);
    if (view === "Month") setCurrentDate(addMonths(currentDate, 1));
    if (view === "Week") setSelectedDate(addWeeks(selectedDate, 1));
    if (view === "Day") setSelectedDate((p) => addDays(p, 1));
  };

  const renderDateLabel = () => {
    if (view === "Week") {
      return `${format(
        startOfWeek(selectedDate, { weekStartsOn: 1 }),
        "d MMM"
      )} - ${format(
        endOfWeek(selectedDate, { weekStartsOn: 1 }),
        "d MMM yyyy"
      )}`;
    }
    if (view === "Day") {
      return format(selectedDate, "MMMM d, yyyy");
    }
    return format(currentDate, "MMMM yyyy");
  };

  const renderDayHeaders = () => (
    <div className="grid grid-cols-7 px-1 pb-3 border-b border-[#E5E7EB] bg-[#F6F6F6] rounded-t-2xl py-3">
      {days.map((day) => (
        <div
          key={day}
          className="text-center text-[14px] text-[#302B2B]"
        >
          {day}
        </div>
      ))}
    </div>
  );

  const handleSummaryChange = (newSummary) => {
    const currencyCode = newSummary.currency || userCurrency || "USD";
    const currencySymbol = getCurrencySymbol(currencyCode);
    
    setSummary({
      price: newSummary.price || 0,
      currency: currencyCode,
      currencySymbol: currencySymbol
    });
  };

  const formatSummaryPrice = (price) => {
    try {
      const priceNum = typeof price === 'string' ? parseFloat(price) : price;
      if (isNaN(priceNum)) return "0.00";
      
      return priceNum.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      console.error("Error formatting summary price:", error);
      return "0.00";
    }
  };

  return (
    <div className="w-full mx-auto bg-white rounded-[20px] p-6 shadow border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 w-full">
        <div className="text-[34px] font-normal w-[14%]">
          {summary.currencySymbol}
          {formatSummaryPrice(summary.price)}
        </div>

        <div className="flex items-center border rounded-full w-[290px] h-[60px] px-5 justify-between">
          {["Day", "Week", "Month"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={clsx(
                "w-[70px] h-[40px] rounded-full text-[14px]",
                view === v
                  ? "bg-[#5563F6] text-white"
                  : "text-[#9A99AB]"
              )}
            >
              {v}
            </button>
          ))}
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="w-[188px] h-[56px] flex items-center justify-center gap-2 bg-[#4B5CFF] hover:bg-[#3f4ee5] text-white rounded-[14px] shadow transition"
        >
          Add Appointment
          <LuCirclePlus className="text-2xl" />
        </button>
      </div>

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <button onClick={handlePrev}>
            <HiOutlineChevronLeft className="text-[#302B2B] text-[20px]" />
          </button>
          <span className="text-[20px] text-[#302B2B] capitalize">
            {renderDateLabel()}
          </span>
          <button onClick={handleNext}>
            <HiOutlineChevronRight className="text-[#302B2B] text-[20px]" />
          </button>
        </div>
      </div>

      {view !== "Day" && renderDayHeaders()}

      <AnimatePresence mode="wait">
        <motion.div
          key={`${view}-${refreshKey}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.25 }}
        >
          {view === "Month" && (
            <MonthView
              token={token}
              currentDate={currentDate}
              selectedDate={selectedDate}
              direction={direction}
              setSelectedDate={setSelectedDate}
              setDayEvents={setDayEvents}
              refreshKey={refreshKey}
              onSummaryChange={handleSummaryChange}
              userCurrency={userCurrency}
            />
          )}

          {view === "Week" && (
            <WeekView
              token={token}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              setDayEvents={setDayEvents}
              onSummaryChange={handleSummaryChange}
              refreshKey={refreshKey}
              userCurrency={userCurrency}
            />
          )}

          {view === "Day" && (
            <DayView
              token={token}
              date={format(selectedDate, "yyyy-MM-dd")}
              onDateChange={setSelectedDate}
              setRefreshKey={setRefreshKey}
              onSummaryChange={handleSummaryChange}
              userCurrency={userCurrency}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <AddAppointmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  );
};

export default Calendar;